import {
  ChatCircle,
  CircleNotch,
  Image as ImageIcon,
} from "@phosphor-icons/react";
import cx from "classnames";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { UnreadDot } from "./UnreadDot";
import { VersionApprovalBadgeView } from "./VersionApprovalBadge";

import {
  interactiveSurfaceClasses,
  interactiveSurfaceIconClasses,
} from "../../../components/InteractiveSurface";
import {
  agentComponentIdAttr,
  CHAT_ATOM_AGENT_COMPONENT_IDS,
} from "../agentComponents/chatAtomRegistry";
import { type VersionApproval } from "../utils/versionApproval";

/** Outline-style Review control — puntt tokens only; never white-on-white hover. */
export function reviewButtonClassName(isOpen = false): string {
  return cx(
    "inline-flex h-8 shrink-0 cursor-pointer items-center justify-center rounded-md border px-3 text-[13px] font-medium no-underline outline-none transition-colors",
    "border-puntt-neutral-gray-6 bg-base-white text-puntt-neutral-gray-12",
    "hover:border-puntt-accent-7 hover:bg-puntt-blue-2 hover:text-puntt-neutral-gray-12",
    "focus-visible:ring-2 focus-visible:ring-puntt-accent-8",
    isOpen &&
      "border-puntt-accent-7 bg-puntt-blue-2 text-puntt-neutral-gray-12",
  );
}

export type ReviewListCardItem = {
  id: string;
  name: string;
  versionNumber?: number;
  thumbnailUrl?: string | null;
  status?: string;
  aiCommentCount?: number;
  humanCommentCount?: number;
  disabled?: boolean;
  isGenerating?: boolean;
  /** Human approval decision on this version (agent-recorded), when decided. */
  approval?: VersionApproval | null;
};

/**
 * Shared review card used by project and standalone chat review rails.
 * Optional `children` (e.g. per-review Participation) render below the
 * open-review control so we never nest interactive controls inside a button.
 */
export function ReviewListCard({
  item,
  onClick,
  testIdPrefix = "project-review-card",
  "data-testid": dataTestId,
  isActive,
  isHighlighted = false,
  hasUnread = false,
  unreadTestId,
  children,
  variant = "row",
  onReviewClick,
  reviewCtaTestId,
  reviewCtaOpen = false,
}: {
  item: ReviewListCardItem;
  onClick?: () => void;
  /** Prefix for `data-testid` attributes, e.g. `project-review-card` or `chat-review-card`. */
  testIdPrefix?: string;
  "data-testid"?: string;
  isActive?: boolean;
  /** Soft emphasis when the matching in-chat approval card is hovered/focused. */
  isHighlighted?: boolean;
  hasUnread?: boolean;
  unreadTestId?: string;
  children?: ReactNode;
  /**
   * `row` = whole-card open control (chat/sidebar).
   * `review-cta` = name cluster + separate Review button (home HoverCard pattern).
   */
  variant?: "row" | "review-cta";
  onReviewClick?: () => void;
  reviewCtaTestId?: string;
  reviewCtaOpen?: boolean;
}) {
  const { t } = useTranslation("ui");
  const cardTestId = dataTestId ?? `${testIdPrefix}-${item.id}`;
  const aiCommentCount = item.aiCommentCount ?? 0;
  const humanCommentCount = item.humanCommentCount ?? 0;
  const isGenerating = item.isGenerating ?? false;
  const disabled = item.disabled ?? isGenerating;

  const ariaLabel = isGenerating
    ? t("reviewListCard.ariaLabel.generating", { name: item.name })
    : isActive
      ? t("reviewListCard.ariaLabel.currentlyViewing", { name: item.name })
      : item.status
        ? t("reviewListCard.ariaLabel.openReviewWithStatus", {
            name: item.name,
            status: item.status,
          })
        : t("reviewListCard.ariaLabel.openReview", { name: item.name });

  const rowOnlyAgentId =
    variant !== "review-cta" && !children
      ? agentComponentIdAttr(CHAT_ATOM_AGENT_COMPONENT_IDS.reviewListCard)
      : {};

  const openControl = (
    <button
      {...rowOnlyAgentId}
      type="button"
      disabled={disabled}
      aria-disabled={disabled}
      aria-current={isActive ? "true" : undefined}
      onClick={() => {
        if (!disabled) onClick?.();
      }}
      aria-label={ariaLabel}
      data-testid={cardTestId}
      className={cx(
        "flex flex-col gap-2 p-3 text-left disabled:cursor-not-allowed disabled:opacity-60",
        variant === "review-cta" ? "min-w-0 flex-1" : "w-full",
        interactiveSurfaceClasses({ interactive: true, disabled }),
        isActive && !children && "ring-2 ring-puntt-accent-8 ring-offset-1",
        isHighlighted &&
          !children &&
          "z-10 scale-[1.03] border-puntt-accent-7 bg-puntt-blue-2 shadow-md ring-2 ring-puntt-accent-8/60 motion-reduce:scale-100",
        children && "rounded-none border-0 shadow-none",
      )}
      data-highlighted={isHighlighted ? "true" : undefined}
    >
      <div
        className="flex items-stretch gap-3"
        data-testid={`${testIdPrefix}-${item.id}-row`}
      >
        <span
          aria-hidden="true"
          data-testid={`${testIdPrefix}-${item.id}-thumbnail`}
          className={interactiveSurfaceIconClasses(
            "flex w-10 shrink-0 items-center justify-center self-stretch overflow-hidden rounded-md bg-puntt-red-9 text-base-white",
          )}
        >
          {item.thumbnailUrl ? (
            // eslint-disable-next-line jsx-a11y/alt-text -- decorative thumbnail; the card carries an aria-label
            <img
              src={item.thumbnailUrl}
              alt=""
              aria-hidden="true"
              loading="lazy"
              decoding="async"
              className="size-full object-cover"
            />
          ) : isGenerating ? (
            <CircleNotch
              size={18}
              className="animate-spin"
              aria-hidden="true"
            />
          ) : (
            <ImageIcon size={18} weight="fill" />
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="block truncate text-[13px] font-semibold text-puntt-neutral-gray-12">
              {item.name}
            </span>
            {hasUnread ? (
              <UnreadDot
                testId={unreadTestId ?? `chat-artifact-unread-${item.id}`}
                ariaLabel={t("reviewListCard.newReview")}
              />
            ) : null}
          </span>
          <span className="mt-1 flex flex-wrap items-center gap-2">
            {item.versionNumber != null && (
              <span
                className="font-mono inline-block rounded border border-puntt-neutral-gray-12 bg-puntt-neutral-gray-12 px-1.5 py-px text-[10px] font-medium text-base-white"
                data-testid={`${testIdPrefix}-${item.id}-version`}
              >
                v{item.versionNumber}
              </span>
            )}
            {item.approval != null && (
              <span
                className="inline-flex"
                data-testid={`${testIdPrefix}-${item.id}-approval`}
              >
                <VersionApprovalBadgeView approval={item.approval} />
              </span>
            )}
            <span
              className="inline-flex items-center gap-1 text-[11px] text-puntt-neutral-gray-10"
              data-testid={`${testIdPrefix}-${item.id}-ai-comments`}
            >
              <ChatCircle size={12} aria-hidden="true" className="shrink-0" />
              {aiCommentCount}{" "}
              {t("reviewListCard.aiCommentLabel", { count: aiCommentCount })}
            </span>
            {humanCommentCount > 0 && (
              <span
                className="inline-flex items-center gap-1 text-[11px] text-puntt-neutral-gray-10"
                data-testid={`${testIdPrefix}-${item.id}-human-comments`}
              >
                <ChatCircle size={12} aria-hidden="true" className="shrink-0" />
                {humanCommentCount}{" "}
                {t("reviewListCard.humanCommentLabel", {
                  count: humanCommentCount,
                })}
              </span>
            )}
          </span>
        </span>
      </div>
    </button>
  );

  if (variant === "review-cta") {
    const ctaTestId = reviewCtaTestId ?? `${testIdPrefix}-${item.id}-review`;
    return (
      <div
        {...agentComponentIdAttr(CHAT_ATOM_AGENT_COMPONENT_IDS.reviewListCard)}
        className="flex items-center gap-3"
        data-testid={`${testIdPrefix}-${item.id}-review-cta`}
      >
        {openControl}
        <button
          type="button"
          disabled={disabled}
          className={reviewButtonClassName(reviewCtaOpen)}
          data-testid={ctaTestId}
          aria-label={t("homeTaskList.reviewAsset", { title: item.name })}
          onClick={() => {
            if (!disabled) onReviewClick?.();
          }}
        >
          {t("homeTaskList.review")}
        </button>
      </div>
    );
  }

  if (!children) {
    return openControl;
  }

  return (
    <div
      {...agentComponentIdAttr(CHAT_ATOM_AGENT_COMPONENT_IDS.reviewListCard)}
      className={cx(
        "rounded-xl",
        isActive && "ring-2 ring-puntt-accent-8 ring-offset-1",
        isHighlighted &&
          "z-10 scale-[1.03] shadow-md ring-2 ring-puntt-accent-8/60 motion-reduce:scale-100",
      )}
      data-testid={`${testIdPrefix}-${item.id}-shell`}
      data-highlighted={isHighlighted ? "true" : undefined}
    >
      <div
        className={cx(
          "flex flex-col overflow-hidden rounded-xl border border-puntt-neutral-gray-5 bg-base-white",
          isHighlighted && "border-puntt-accent-7",
        )}
        data-testid={`${testIdPrefix}-${item.id}-shell-inner`}
      >
        {openControl}
        {children}
      </div>
    </div>
  );
}
